/**
 * partnerPlans.js — планы продаж по партнёрам (Partner CRM).
 *
 * Вынесено из partnerCrm.js: тот файл упёрся в baseline размера компонента
 * (гейт check_component_size), а планы — самостоятельная предметная область
 * со своим набором операций и своей валютной семантикой.
 *
 * Валюта: план ставится в THB, но отдельные планы партнёров заведены и в USDT,
 * и в рублях. Сведение к единой валюте делает СЕРВЕР (`planSummary`) — тем же
 * курсом периода, что и ведомость (payroll_fx_service: ручной override, иначе
 * авто). Клиент не конвертирует сам и не отбрасывает валюты молча.
 */
import { apiFetch } from './core'

const BASE = '/partner-crm'
const MANAGER_BASE = '/partner-crm/manager-plans'

export const partnerPlans = {
    listForPartner: (partnerId) => apiFetch(`${BASE}/partners/${partnerId}/plans`),
    listForPeriod: (period) => apiFetch(`${BASE}/plans?period=${period}`),

    /**
     * Сводка плана за месяц в единой валюте (THB) — пересчёт на сервере.
     * Ответ содержит `rates` и `unconverted`: курс видно в интерфейсе, а валюта
     * без курса не подмешивается в итог по номиналу.
     */
    summary: (period, managerId) => {
        const qs = new URLSearchParams({ period })
        if (managerId) qs.set('manager_id', managerId)
        return apiFetch(`${BASE}/plans/summary?${qs}`)
    },

    /**
     * План месяца, базовая линия действующих партнёров, разрыв и его раскладка
     * по именам — одним ответом на весь экран «План месяца».
     */
    gap: (period, managerId, months) => {
        const qs = new URLSearchParams({ period })
        if (managerId) qs.set('manager_id', managerId)
        if (months) qs.set('months', String(months))
        return apiFetch(`${BASE}/plan-gap?${qs}`)
    },

    /**
     * Финансовые поля для СТРАНИЦЫ таблицы партнёров — одной пачкой, не запросом
     * на строку. Считает тот же сервис, что и `gap`, поэтому цифры на двух экранах
     * совпадают. Ходить сюда можно только из представления «Оборот и потенциал»:
     * за данными сервер идёт во внешнюю систему, и обычный список ждать её не должен.
     */
    financials: (partnerIds, period, months) => {
        const qs = new URLSearchParams({ period })
        for (const id of partnerIds) qs.append('partner_id', id)
        if (months) qs.set('months', String(months))
        return apiFetch(`${BASE}/partner-financials?${qs}`)
    },

    // ── Планы оборота МЕНЕДЖЕРА ───────────────────────────────────────────────
    // Другая сущность, чем планы выше: там план партнёру по объёму и марже, здесь —
    // план оборота человеку на месяц, от которого экран «План месяца» считает разрыв.
    // Жили в этом же модуле и были снесены при переписывании файла под планы
    // партнёров (#3829), хотя ManagerPlanModal продолжал их звать.

    /** Планы всех менеджеров отдела на месяц + дефолты филиалов. */
    list: (period) => apiFetch(`${MANAGER_BASE}?period=${encodeURIComponent(period)}`),

    /** planThb = null снимает ручной план и возвращает менеджера к дефолту филиала. */
    setForManager: (managerId, period, planThb) =>
        apiFetch(`${MANAGER_BASE}/${managerId}?period=${encodeURIComponent(period)}`, {
            method: 'PUT',
            body: JSON.stringify({ plan_turnover_thb: planThb }),
        }),

    /** Дефолт филиала. Уже поставленные ручные планы не переписывает — это делает сервер. */
    setCityDefault: (city, planThb) =>
        apiFetch(`${MANAGER_BASE}/defaults/city`, {
            method: 'PUT',
            body: JSON.stringify({ city, plan_turnover_thb: planThb }),
        }),

    create: (data) => apiFetch(`${BASE}/plans`, { method: 'POST', body: JSON.stringify(data) }),
    patch: (id, data) => apiFetch(`${BASE}/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    remove: (id) => apiFetch(`${BASE}/plans/${id}`, { method: 'DELETE' }),
}

export default partnerPlans
